import { api, ApiError, ORG_ROLES } from '../lib/api.js';
import { el, render, fmtDateOnly, fmtRelative, roleLabel, sortableHeader, memberStatusBadges } from '../lib/dom.js';
import { canWrite } from '../lib/session.js';
import {
  pageHeader,
  avatar,
  roleChip,
  searchField,
  filterSelect,
  actionMenu,
  renderPagination,
  skeletonRows,
  emptyState,
  errorState,
  fullRow,
  toast,
} from '../lib/ui.js';
import { accountActions, restoredPanel } from './account-actions.js';

// ---------------------------------------------------------------------------
// Every user across every organisation, one row per membership (a person can
// belong to more than one org — see listAllUsers' doc comment on the API
// side). Search, filters, sort, and paging are all server-side.
//
// Account-wide actions (deactivate / delete / restore the ACCOUNT) live here
// and on the user's own profile — never in an organisation's member list,
// where a row reads as "this person in this org" and an account-wide verb
// would close every other tenant they belong to as well.
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;

export function usersView({ navigate, search = '' }) {
  const state = {
    // Prefilled when arrived at from elsewhere with ?search=, so the operator
    // lands on the person they were already looking at.
    search,
    includeRemoved: false,
    role: '',
    status: 'any',
    joined: 'all',
    sortBy: 'joinedAt',
    sortDir: 'desc',
    page: 1,
    total: 0,
  };

  const tbody = el('tbody', {});
  const footer = el('div', {});
  const theadRow = el('tr', {});
  const resultSlot = el('div', {});
  const cols = () => (canWrite() ? 7 : 6);

  const reload = () => {
    state.page = 1;
    void load();
  };

  const searchBox = searchField({
    placeholder: 'Search name, email or org…',
    label: 'Search users',
    value: search,
    onSearch: (v) => {
      state.search = v;
      reload();
    },
  });

  const removedToggle = el('input', {
    type: 'checkbox',
    onchange: (e) => {
      state.includeRemoved = e.target.checked;
      reload();
    },
  });

  function row(u) {
    const account = canWrite()
      ? accountActions(u, {
          onDone: (message, restored) => {
            toast(message);
            if (restored) {
              render(resultSlot, restoredPanel(restored));
              resultSlot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
            void load();
          },
        })
      : [];

    return el(
      'tr',
      { class: 'is-clickable', onclick: (e) => !e.target.closest('button') && navigate(`/users/${u.userId}`) },
      el(
        'td',
        {},
        el(
          'div',
          { class: 'cell-main' },
          avatar(u.fullName, { size: 36 }),
          el('div', { class: 'cell-main__text' }, el('span', { class: 'cell-title' }, u.fullName), el('span', { class: 'cell-sub' }, u.email)),
        ),
      ),
      el(
        'td',
        {},
        el(
          'div',
          { class: 'cell-badges' },
          el(
            'button',
            {
              type: 'button',
              class: 'link-btn nowrap',
              title: `Open ${u.orgName}. Its member list is where you change ${u.fullName}'s role in this organisation, or remove them from it without touching their account.`,
              onclick: () => navigate(`/orgs/${u.orgId}?tab=members`),
            },
            u.orgName,
          ),
          u.orgDeleted ? el('span', { class: 'badge badge--red' }, 'Deleted') : null,
        ),
      ),
      el('td', {}, roleChip(roleLabel(u.role))),
      el(
        'td',
        {},
        el(
          'div',
          { class: 'cell-badges' },
          memberStatusBadges(u),
          u.mfaEnabled ? el('span', { class: 'role-chip' }, 'MFA') : null,
          u.authProvider === 'AZURE_AD' ? el('span', { class: 'role-chip' }, 'SSO') : null,
        ),
      ),
      el('td', { class: 'nowrap' }, u.lastLoginAt ? fmtRelative(u.lastLoginAt) : 'Never'),
      el('td', { class: 'nowrap' }, fmtDateOnly(u.joinedAt)),
      canWrite()
        ? el(
            'td',
            { class: 'col-actions' },
            actionMenu(
              [
                { label: 'View profile', iconName: 'menu-view', onSelect: () => navigate(`/users/${u.userId}`) },
                { label: `Open ${u.orgName}`, iconName: 'nav-organisations', onSelect: () => navigate(`/orgs/${u.orgId}?tab=members`) },
                ...account,
              ],
              { label: `Actions for ${u.fullName}` },
            ),
          )
        : null,
    );
  }

  async function load() {
    render(
      theadRow,
      sortableHeader(state, 'fullName', 'User', reload),
      sortableHeader(state, 'orgName', 'Organisation', reload),
      sortableHeader(state, 'role', 'Role', reload),
      sortableHeader(state, 'isActive', 'Status', reload),
      sortableHeader(state, 'lastLoginAt', 'Last login', reload),
      sortableHeader(state, 'joinedAt', 'Joined', reload),
      canWrite() ? el('th', { class: 'col-actions' }, '') : null,
    );
    render(tbody, skeletonRows(cols(), 6));
    try {
      const data = await api.listAllUsers({
        search: state.search,
        includeRemoved: state.includeRemoved,
        role: state.role || undefined,
        status: state.status,
        joined: state.joined,
        sortBy: state.sortBy,
        sortDir: state.sortDir,
        page: state.page,
        pageSize: PAGE_SIZE,
      });
      state.total = data.total;
      const filtered = state.search !== '' || state.role !== '' || state.status !== 'any' || state.joined !== 'all';
      render(
        tbody,
        data.users.length === 0
          ? fullRow(
              cols(),
              emptyState({
                title: filtered ? 'No users match' : 'No users yet',
                text: filtered ? 'Try a different search or clear the filters.' : 'Users appear here once they are added to an organisation.',
                compact: true,
              }),
            )
          : data.users.map(row),
      );
      renderPagination(footer, {
        page: state.page,
        pageSize: PAGE_SIZE,
        total: state.total,
        noun: 'memberships',
        onPage: (n) => {
          state.page = n;
          void load();
        },
      });
    } catch (err) {
      render(tbody, fullRow(cols(), errorState({ title: 'Failed to load users', text: err instanceof ApiError ? err.message : 'Check the API and try again.', onRetry: () => void load() })));
      render(footer);
    }
  }

  void load();

  return el(
    'div',
    { class: 'stack' },
    pageHeader({
      title: 'Users',
      subtitle:
        'Every user across every organisation. A person in more than one organisation has one row per membership. ' +
        'Open a user for their full profile and account-wide actions.',
    }),
    el(
      'div',
      { class: 'toolbar' },
      searchBox.root,
      filterSelect({
        label: 'Role',
        value: state.role,
        options: [{ value: '', label: 'All roles' }, ...ORG_ROLES.map((r) => ({ value: r, label: roleLabel(r) }))],
        onChange: (v) => {
          state.role = v;
          reload();
        },
      }),
      filterSelect({
        label: 'Status',
        value: state.status,
        options: [
          { value: 'any', label: 'Any status' },
          { value: 'active', label: 'Active' },
          { value: 'pending', label: 'Pending activation' },
          { value: 'deactivated', label: 'Deactivated' },
          { value: 'removed', label: 'Removed' },
        ],
        onChange: (v) => {
          state.status = v;
          reload();
        },
      }),
      filterSelect({
        label: 'Date joined',
        value: state.joined,
        options: [
          { value: 'all', label: 'Any time' },
          { value: '30', label: 'Last 30 days' },
          { value: '90', label: 'Last 90 days' },
          { value: '365', label: 'Last 12 months' },
        ],
        onChange: (v) => {
          state.joined = v;
          reload();
        },
      }),
      el('label', { class: 'checkbox-field toolbar__end' }, removedToggle, el('span', { class: 'box' }), 'Show removed'),
    ),
    resultSlot,
    el('div', { class: 'table-card' }, el('div', { class: 'table-scroll' }, el('table', { class: 'table' }, el('thead', {}, theadRow), tbody)), footer),
  );
}
